<!--
  Базовый просмотр вложения: единый preview для чатов, форм и списков поверх BaseModal.
-->
<template>
  <BaseModal
    :visible="true"
    :title="displayName"
    size="fullscreen"
    body-padding="none"
    @close="$emit('close')"
  >
    <div class="base-attachment-preview">
      <img
        v-if="attachmentIsImage"
        :src="previewUrl"
        :alt="displayName"
        class="base-attachment-preview__image"
      />

      <MarkdownPreview v-else-if="attachmentIsMarkdown" :url="previewUrl" :name="displayName" />

      <slot
        v-else-if="attachmentIsAudio && $slots.audio"
        name="audio"
        :attachment="attachment"
        :display-name="displayName"
        :preview-url="previewUrl"
      />

      <div v-else class="base-attachment-preview__file">
        <div
          class="base-attachment-preview__file-icon"
          :class="fileIconClass"
        >
          <span class="base-attachment-preview__extension">
            {{ fileExtension }}
          </span>
        </div>

        <div class="base-attachment-preview__info">
          <h3>{{ displayName }}</h3>
          <p>Тип: {{ fileTypeName }}</p>
          <p>Размер: {{ displaySize }}</p>
          <a
            class="base-attachment-preview__download"
            :href="previewUrl"
            target="_blank"
            :download="displayName"
          >
            Открыть
          </a>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import MarkdownPreview from "@/components/Attachment/MarkdownPreview.vue";
import {
  attachmentName,
  attachmentOpenUrl,
  attachmentSize,
  formatFileSize,
  getFileExtension,
  getFileIconClass,
  getFileTypeName,
  isAudio,
  isImage,
  isMarkdown,
} from "@/utils/fileUtils";

export default {
  name: "BaseAttachmentPreview",
  components: { MarkdownPreview },

  props: {
    attachment: {
      type: Object,
      required: true,
    },
  },

  emits: ["close"],

  computed: {
    attachmentIsImage() {
      return isImage(this.attachment);
    },

    attachmentIsMarkdown() { return isMarkdown(this.attachment); },
    attachmentIsAudio() {
      return isAudio(this.attachment);
    },

    previewUrl() {
      return attachmentOpenUrl(this.attachment);
    },

    displayName() {
      return attachmentName(this.attachment) || "Вложение";
    },

    displaySize() {
      return formatFileSize(attachmentSize(this.attachment));
    },

    fileExtension() {
      return getFileExtension(this.attachment);
    },

    fileIconClass() {
      return getFileIconClass(this.attachment);
    },

    fileTypeName() {
      return getFileTypeName(this.attachment);
    },
  },
};
</script>

<style scoped>
.base-attachment-preview {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #111827;
}

.base-attachment-preview__image {
  display: block;
  max-width: 100%;
  max-height: calc(100dvh - 140px);
  border-radius: 8px;
  object-fit: contain;
  box-shadow: 0 14px 42px rgba(0, 0, 0, 0.35);
}

.base-attachment-preview__file {
  width: min(360px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 22px;
  border-radius: 8px;
  background: #fff;
  color: #333;
  text-align: center;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
}

.base-attachment-preview__file-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 6px;
  border-radius: 8px;
}

.base-attachment-preview__file-icon.image-icon {
  background-color: #e3f2fd;
  color: #1976d2;
}

.base-attachment-preview__file-icon.pdf-icon {
  background-color: #ffebee;
  color: #f44336;
}

.base-attachment-preview__file-icon.word-icon {
  background-color: #e8f5e9;
  color: #4caf50;
}

.base-attachment-preview__file-icon.excel-icon {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.base-attachment-preview__file-icon.archive-icon {
  background-color: #f3e5f5;
  color: #9c27b0;
}

.base-attachment-preview__file-icon.audio-icon {
  background-color: #ecfeff;
  color: #0891b2;
}

.base-attachment-preview__file-icon.text-icon {
  background-color: #e0f7fa;
  color: #00bcd4;
}

.base-attachment-preview__file-icon.default-icon {
  background-color: #f5f5f5;
  color: #616161;
}

.base-attachment-preview__extension {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 80px;
  padding: 3px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.base-attachment-preview__info {
  width: 100%;
}

.base-attachment-preview__info h3 {
  margin: 0 0 8px;
  color: #222;
  font-size: 15px;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.base-attachment-preview__info p {
  margin: 4px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.3;
}

.base-attachment-preview__download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  padding: 8px 14px;
  border-radius: 6px;
  background: var(--color-green);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
}

@media (max-width: 767px) {
  .base-attachment-preview {
    padding: 16px;
  }

  .base-attachment-preview__image {
    max-height: calc(100dvh - 120px);
  }
}
</style>
